import { action, IReactionDisposer, IReactionPublic, reaction } from "mobx";

type Nullable<T> = T | null;
type Optional<T> = T | null | undefined;

type ClassNameDataType = [Optional<string>] | [Optional<boolean>, Optional<string>, Optional<string>?];


function generateGuid(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Await for seconds
function waitSecondsAsync(seconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

// TODO add timeout support
function playAndWaitAsync(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio(url);

    audio.addEventListener("ended", () => resolve());
    audio.addEventListener("error", (e) => reject(e));

    audio.play().catch(reject);
  });
}

function waitReactionAsync<T>(ct: CancellationToken, expression: (r: IReactionPublic) => T, effect: (arg: T) => boolean): Promise<void> {
  return new Promise((resolve, reject) => {

    if (ct.isCancelled) {
      reject(new Error('Cancelled'));
      return;
    }

    const handleCancellationTokenCancel = () => {
      reactionDisposer();
      reject(new Error('Cancelled'));
    }

    const reactionDisposer = reaction(expression, (arg) => {

      if (ct.isCancelled) {
        reactionDisposer();
        reject(new Error('Cancelled'));
        return;
      }

      if (effect(arg)) {
        reactionDisposer();
        ct.OnCancel.delete(handleCancellationTokenCancel);
        resolve();
      }
    });

    ct.OnCancel.add(handleCancellationTokenCancel);
  });
}

class WithMobXAction {
  @action
  action(callback: (self: this) => void) {
    callback(this);
  }
}

export class CancellationToken {
  private _cancelled: boolean = false;
  public readonly OnCancel: { add: (_: () => void) => void, delete: (_: () => void) => void } = new Set();

  get isCancelled(): boolean {
    return this._cancelled;
  }

  cancel() {
    this._cancelled = true;
    const onCancel = this.OnCancel as Set<() => void>;
    onCancel.forEach(reject => reject());
    onCancel.clear();
  }
}

export class RandomUrl<T> {

  /// urlWithKey should contain '[key]' to be replaced
  constructor(public readonly urlWithKey: string, public readonly data: { [key: string]: T }) { }

  getRandom(): { url: string, value: T } {
    const keysArray = Object.keys(this.data);
    const randomIndex = Math.floor(Math.random() * keysArray.length);
    const key = keysArray[randomIndex];
    return {
      url: this.urlWithKey.replace('[key]', key),
      value: this.data[key]
    };
  }
}

export class RandomUrlWithWeights<T extends { weight: number }> {

  /// urlWithKey should contain '[key]' to be replaced
  constructor(public readonly urlWithKey: string, public readonly data: { [key: string]: T }) {
    if (Object.keys(this.data).length === 0) {
      throw new Error('Data object cannot be empty');
    }
  }

  getRandomUnweighted(): { url: string, value: T } {
    const keysArray = Object.keys(this.data);
    const randomIndex = Math.floor(Math.random() * keysArray.length);
    const key = keysArray[randomIndex];
    return {
      url: this.urlWithKey.replace('[key]', key),
      value: this.data[key]
    };
  }

  // minWeight = minimum weight to consider
  getRandom(minWeight: number = 0): { url: string, value: T } {
    const keysArray = Object.keys(this.data);

    let filteredTotalWeight = 0;
    const filteredKeys = keysArray.filter(key => {
      const weight = this.data[key].weight;
      if (weight >= minWeight) {
        filteredTotalWeight += weight;
        return true;
      }
      return false;
    });

    if (filteredKeys.length === 0) {
      // If no keys meet the minimum weight then fallback to the maximum weight
      const maxWeight = Math.max(...keysArray.map(key => this.data[key].weight));
      return this.getRandom(maxWeight);
    }

    let random = Math.random() * filteredTotalWeight;

    for (const key of filteredKeys) {
      random -= this.data[key].weight;
      if (random <= 0) {
        return {
          url: this.urlWithKey.replace('[key]', key),
          value: this.data[key]
        };
      }
    }

    // Fallback (should never reach here, but TypeScript needs it)
    const randomIndex = Math.floor(Math.random() * filteredKeys.length);
    const key = filteredKeys[randomIndex];
    return {
      url: this.urlWithKey.replace('[key]', key),
      value: this.data[key]
    };
  }
}

export const async = {
  waitSecondsAsync,
  playAndWaitAsync,
  waitReactionAsync
};

export const utils = {
  generateGuid,
  mapMap<K, V, U>(map: Map<K, V>, callback: (value: V, key: K) => U): U[] {
    const result: U[] = [];
    // map.forEach((value, key) => {
    //   result.push(callback(value, key));
    // });
    for (const [key, value] of map.entries()) {
      result.push(callback(value, key));
    }
    return result;
  },

  copyToClipboardAnimatedAsync: async (text: string, propSetter: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      propSetter(true);
      await async.waitSecondsAsync(2);
      propSetter(false);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  },

  // https://coureywong.medium.com/how-to-shuffle-an-array-of-items-in-javascript-39b9efe4b567
  shuffle<T>(array: T[]): T[] {
    let i = array.length;
    const result = [...array];
    while (--i > 0) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  },
  // className(input: [boolean | undefined | null, string][]): { className: string } {
  //   let result = '';
  //   input.forEach(([condition, className]) => {
  //     if (condition) {
  //       if (result.length > 0) {
  //         result += ' ';
  //       }
  //       result += className;
  //     }
  //   });

  //   return { className: result };
  // },
  className(input: ClassNameDataType[]): string {
    let result = '';
    // [condition, classNameTrue, classNameFalse]
    input.forEach((argsArray) => {
      if (!argsArray) return;

      let innerResult = null;

      if (argsArray.length === 1) {
        innerResult = argsArray[0];
      } else {
        const [condition, classNameTrue, classNameFalse] = argsArray;
        if (condition) {
          innerResult = classNameTrue;
        } else {
          innerResult = classNameFalse;
        }
      }

      if (innerResult) {
        if (result.length > 0) result += " ";
        result += innerResult;
      }
    });

    return result;
  },

  WithMobXAction
};

// Types


// Strict Omit that errors if key doesn't exist
export type StrictOmit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
